// TODO: Replace this calendar component with the native calendars in JT-114815
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dimensions, StyleSheet, View } from 'react-native';
import { CalendarList, DateData, LocaleConfig } from 'react-native-calendars';
import { Theme } from 'react-native-calendars/src/types';
import {
  generateSelectedDateObject,
  toDateString,
} from 'shared/components/vxl/CalendarPicker/CalendarPicker.helpers';
import DaysHeader from 'shared/components/vxl/CalendarPicker/DaysHeader';
import { isWeb } from 'shared/constants/platform';
import theme from 'shared/styles/theme';
import { VaroFontNames } from 'shared/styles/typography';

interface CalendarPickerProps {
  /** Used to have pre-selected initial range of dates Type is an array of date strings */
  initialRange?: string[];

  /** Used to show holiday/disabled days list Also an array of date strings */
  holidayList?: string[];

  /** This is a callback function for getting the user selection on the calendar */
  onDateListener?: (date: string) => void;

  /** This is minimum selectable date that user can select, prior dates are in disable state */
  minDate?: string;

  /** This is maximum selectable date that user can select, post this date other dates are in disable state */
  maxDate?: string;

  /** This allows user to scroll/swipe for number of months in past  */
  pastScrollRange?: number;

  /** This allows user to scroll/swipe for number of months in future  */
  futureScrollRange?: number;

  /** Give custom width */
  calendarWidth?: number;

  /** This allows date picker to reselect each new day rather than a range.  */
  isSingleSelect?: boolean;

  /** This allows date picker to show days label.  */
  showDayLabel?: boolean;

  /** This allows date picker to move to specific month in view.  */
  viewedDate?: string;

  userSelectedDate?: string;
}

const windowWidth = Dimensions.get('window').width;

const CalendarPicker = (props: CalendarPickerProps) => {
  const {
    initialRange,
    holidayList = [],
    onDateListener,
    minDate,
    maxDate,
    pastScrollRange,
    futureScrollRange,
    isSingleSelect = true,
    showDayLabel = true,
    viewedDate,
    calendarWidth,
    userSelectedDate = '',
  } = props;

  const [startDate, setStartDate] = useState(
    initialRange && initialRange.length > 0 ? initialRange[0] : null,
  );
  const [endDate, setEndDate] = useState<string | null>(
    initialRange && initialRange.length > 0 ? initialRange[1] : null,
  );
  const [selectingStartDate, setSelectingStartDate] = useState(true);

  const [selected, setSelected] = useState(userSelectedDate);

  const [viewWidth, setViewWidth] = useState(windowWidth);

  const componentWidth = () => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setViewWidth(calendarWidth ?? window.width);
    });
    return () => subscription?.remove();
  };

  const currentDate = viewedDate ?? minDate ?? minDate;

  const { t: translatedText } = useTranslation('calendar', {
    keyPrefix: 'calendar',
  });

  LocaleConfig.locales.en = {
    monthNames: [
      translatedText('january'),
      translatedText('february'),
      translatedText('march'),
      translatedText('april'),
      translatedText('may'),
      translatedText('june'),
      translatedText('july'),
      translatedText('august'),
      translatedText('september'),
      translatedText('october'),
      translatedText('november'),
      translatedText('december'),
    ],
    monthNamesShort: [
      translatedText('jan'),
      translatedText('feb'),
      translatedText('mar'),
      translatedText('apr'),
      translatedText('may'),
      translatedText('jun'),
      translatedText('jul'),
      translatedText('aug'),
      translatedText('sep'),
      translatedText('oct'),
      translatedText('nov'),
      translatedText('dec'),
    ],
    dayNames: [
      translatedText('sunday'),
      translatedText('monday'),
      translatedText('tuesday'),
      translatedText('wednesday'),
      translatedText('thursday'),
      translatedText('friday'),
      translatedText('saturday'),
    ],
    dayNamesShort: [
      translatedText('sun'),
      translatedText('mon'),
      translatedText('tue'),
      translatedText('wed'),
      translatedText('thu'),
      translatedText('fri'),
      translatedText('sat'),
    ],
  };

  LocaleConfig.defaultLocale = 'en';

  const holidayListObject = holidayList.reduce((holidayDateObj, date) => {
    const dateString = toDateString(new Date(date));
    holidayDateObj[dateString] = {
      disabled: true,
      disabledTouchEvent: true,
    };
    return holidayDateObj;
  }, {});

  const markedDates = generateSelectedDateObject(
    startDate,
    endDate,
    holidayListObject,
  );

  const selectedDate = {
    [selected]: {
      selected: true,
      disableTouchEvent: true,
      selectedColor: theme.color.interactive.primary,
      customStyles: {
        container: { borderRadius: 5 },
      },
    },
  };

  const marked = { ...selectedDate, ...markedDates, ...holidayListObject };

  const onDayPress = useCallback(
    (day: DateData) => {
      if (!isSingleSelect) {
        setSelectingStartDate(!selectingStartDate);
        if (selectingStartDate) {
          setEndDate(null);
          setStartDate(day.dateString);
        } else {
          setEndDate(day.dateString);
        }
      }
      setSelected(day.dateString);
      if (onDateListener) {
        onDateListener(day.dateString);
      }
    },
    [onDateListener, selectingStartDate, isSingleSelect],
  );

  const themeWithDaysLabel = {
    'stylesheet.day.basic': {
      today: {
        ...theme.typography.body.base,
        borderColor: theme.color.expressive.orchid,
        borderWidth: 1,
        borderRadius: 5,
      },
      todayText: {
        ...theme.typography.body.base,
        color: theme.color.interactive.primary,
      },
      text: {
        ...theme.typography.body.base,
        margin: 5,
      },
    },

    'stylesheet.calendar.header': {
      monthText: {
        ...theme.typography.body.baseBold,
        color: theme.color.text.primary,
        fontFamily: VaroFontNames.NeueHaasGroteskDisplayProMediumNormal,
      },
      dayHeader: {
        ...theme.typography.body.xs,
        marginTop: theme.spacing.xl,
        marginBottom: theme.spacing.m,
        textAlign: 'center',
        color: theme.color.text.primary,
      },
      ...(showDayLabel ? {} : { week: { height: 0 } }),
    },
  };

  return (
    <View style={styles.container} onLayout={componentWidth}>
      {showDayLabel ? null : <DaysHeader />}
      <CalendarList
        testID="calendarList"
        calendarWidth={isWeb ? undefined : viewWidth}
        calendarStyle={
          isWeb && calendarWidth === undefined ? styles.webCalendar : undefined
        }
        current={currentDate}
        theme={themeWithDaysLabel as Theme}
        hideExtraDays
        minDate={minDate}
        maxDate={maxDate}
        pastScrollRange={pastScrollRange}
        futureScrollRange={futureScrollRange}
        onDayPress={(day) => {
          onDayPress(day);
        }}
        markingType="custom"
        markedDates={marked}
        accessible
        {...props}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  webCalendar: {
    width: 'auto',
  },
});

export default CalendarPicker;
